import React, { useEffect } from "react";
import type { AppProps } from "next/app";
import { SessionProvider, useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { ThemeProvider } from "next-themes";
import axios from "axios";
import LoadingSpinner from "../components/LoadingSpinner";
import { I18nProvider } from "@/i18n";
import { handleUnauthorized } from "@/lib/http/fetchJson";
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import "../styles/globals.css";
// Global Snow theme styles for React Quill editors (must live in _app for pages router)
import "react-quill/dist/quill.snow.css";

const AUTH_GUARD_DISABLED = process.env.NEXT_PUBLIC_BYPASS_AUTH_GUARD === "1";
const SESSION_EXPIRED_REASON = "session_expired";
let isHandlingUnauthorized = false;
let axiosInterceptorId: number | null = null;
let originalFetchRef: typeof window.fetch | null = null;
let hasWrappedFetch = false;

export default function App({
  Component,
  pageProps: { session, ...pageProps },
}: AppProps) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { refetchOnWindowFocus: false, retry: false },
        },
      })
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hasWrappedFetch) {
      originalFetchRef = window.fetch.bind(window);
      window.fetch = async (input, init) => {
        const response = await originalFetchRef!(input, init);
        const responseClone = response.clone();
        try {
          const requestUrl =
            typeof input === "string"
              ? input
              : input instanceof Request
                ? input.url
                : input.toString();
          const parsedUrl = new URL(requestUrl, window.location.href);
          const isSameOrigin = parsedUrl.origin === window.location.origin;
          const path = parsedUrl.pathname;
          const isApiRequest =
            isSameOrigin &&
            path.startsWith("/api/") &&
            !path.startsWith("/api/auth/");
          if (isApiRequest && responseClone.status === 401 && !isHandlingUnauthorized) {
            isHandlingUnauthorized = true;
            void handleUnauthorized(SESSION_EXPIRED_REASON);
          }
        } catch {
          // ignore URL parsing failures
        }
        return response;
      };
      hasWrappedFetch = true;
    }

    if (axiosInterceptorId === null) {
      axiosInterceptorId = axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (error?.response?.status === 401 && !isHandlingUnauthorized) {
            isHandlingUnauthorized = true;
            await handleUnauthorized(SESSION_EXPIRED_REASON);
          }
          return Promise.reject(error);
        }
      );
    }

    return () => {
      if (axiosInterceptorId !== null) {
        axios.interceptors.response.eject(axiosInterceptorId);
        axiosInterceptorId = null;
      }
      if (hasWrappedFetch && originalFetchRef) {
        window.fetch = originalFetchRef;
        originalFetchRef = null;
        hasWrappedFetch = false;
      }
    };
  }, []);

  return (
    <ThemeProvider attribute="class" defaultTheme="system">
      <SessionProvider session={session}>
        <QueryClientProvider client={queryClient}>
          <I18nProvider
            initialLocale={session?.user?.preferred_locale}
            initialTimezone={session?.user?.timezone}
          >
            <AuthGuard>
              <LoadingSpinner />
              <Component {...pageProps} />
            </AuthGuard>
            <ReactQueryDevtools initialIsOpen={false} />
          </I18nProvider>
        </QueryClientProvider>
      </SessionProvider>
    </ThemeProvider>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  // TECH DEBT: Global redirect for unauthenticated users until full route guards are implemented.
  if (AUTH_GUARD_DISABLED) {
    return <>{children}</>;
  }

  const { status } = useSession();
  const router = useRouter();
  const redirectedRef = React.useRef(false);
  const publicRoutes = new Set(["/login", "/signup", "/register", "/verify-email"]);
  const isPublicRoute =
    publicRoutes.has(router.pathname) || router.pathname.startsWith("/auth/");

  useEffect(() => {
    if (status === "unauthenticated" && !isPublicRoute) {
      // Use replace and a ref so we don't redirect repeatedly
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        router.replace("/login");
      }
    }
  }, [status, router, isPublicRoute]);

  return <>{children}</>;
}
